<template>
  <div>
    <transition mode="out-in">
      <div v-if="modelValue.itemid">
        <div class="d-flex align-items-start">
          <img v-if="modelValue.photo_url" :src="modelValue.photo_url"
               style="width: 60px; height: 60px; object-fit: cover;"
               class="border rounded me-3">
          <div class="flex-grow-1">
            <div><strong>{{ modelValue.memo }}</strong></div>
            <div v-if="!modelValue.is_custom && parseInt(modelValue.available_quantity ?? 1) === 0" class="small text-warning fw-semibold mt-1">
              ⚠ 0 available — item may be delisted
            </div>
            <div class="small text-muted">
              <div><strong>SKU:</strong> {{ modelValue.SKU }}</div>
              <div><strong>Item ID:</strong> {{ modelValue.itemid }}</div>
              <div><strong>COGS:</strong> <money-block :dollar="parseFloat(modelValue.cogs)" class="d-inline-block" /></div>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearItem">
            Change
          </button>
        </div>
      </div>
      <div v-else-if="quickAddMode" key="quick-add">
        <quick-add-item
          :key="quickAddKey"
          :initial-title="itemSearch"
          :initial-data="quickAddData"
          @cancel="cancelQuickAdd"
          @commit="onQuickAddCommit"
        />
      </div>
      <div v-else class="item-search">
        <input
          type="text"
          class="form-control"
          v-model="itemSearch"
          placeholder="Search for item by title or SKU..."
          @focus="showResults = true"
        >
        <div class="form-check mt-1">
          <input class="form-check-input" type="checkbox" id="include-delisted" v-model="includeDelisted">
          <label class="form-check-label small text-muted" for="include-delisted">Include delisted items</label>
        </div>
        <transition mode="out-in">
          <loading-spinner v-if="searching" :small="true" class="my-3" />
          <div v-else-if="showResults && searchResults.length > 0" class="search-results list-group mt-2">
            <div
              v-for="result in searchResults"
              :key="result.remote_id"
              role="button"
              class="list-group-item list-group-item-action d-flex"
              @click="selectItem(result)"
            >
              <img
                v-if="result.photo_url"
                :src="result.photo_url"
                style="width: 80px; height: 80px; object-fit: cover;"
                class="border rounded me-3"
              >
              <div class="flex-grow-1">
                <div><strong>{{ result.title }}</strong></div>
                <div v-if="parseInt(result.available_quantity ?? 0) === 0" class="small text-warning fw-semibold">
                  ⚠ 0 available — may be delisted
                </div>
                <div class="small text-muted">
                  <div><strong>SKU:</strong> {{ result.SKU }}</div>
                  <div><strong>QTY:</strong> {{ parseInt(result.available_quantity ?? 0) }}</div>
                  <div><strong>COGS:</strong> <money-block :dollar="-parseFloat(result.source_price)" class="d-inline-block" /></div>
                  <div><strong>Listed Price:</strong> <money-block :dollar="parseFloat(result.sale_price)" class="d-inline-block" /></div>
                  <div><strong>Item ID:</strong> {{ result.remote_id }}</div>
                </div>
              </div>
            </div>
            <div class="alert alert-info mt-2 small text-center">Can't find it? <a href="#" @click.prevent="quickAdd">Quick add a custom item</a></div>
          </div>
          <div v-else-if="showResults && itemSearch.length > 0 && searchResults.length === 0" class="alert alert-info mt-2 small text-center">
            No matching items. <a href="#" @click.prevent="quickAdd">Quick add this as a custom item →</a>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineEmits, defineProps } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue"
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue"
import QuickAddItem from "@/app/components/orders/QuickAddItem.vue"

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()
const includeDelisted = computed({
  get: () => store.state.eshop.includeDelistedSearch,
  set: val => store.commit('eshop/setIncludeDelistedSearch', val)
})
const itemSearch = ref('')
const searching = ref(false)
const searchResults = ref([])
const showResults = ref(false)
const quickAddMode = ref(false)
const quickAddData = ref(null)
const quickAddKey = ref(0)

function quickAdd() {
  quickAddData.value = null
  quickAddKey.value++
  quickAddMode.value = true
  showResults.value = false
}

function cancelQuickAdd() {
  quickAddMode.value = false
  // Clear any custom item data from the line item
  if (quickAddData.value) {
    emit('update:modelValue', {
      ...props.modelValue,
      itemid: '',
      SKU: '',
      memo: '',
      cogs: 0,
      photo_url: '',
      unit_price: 0,
      is_custom: false,
      source_price: 0,
      acquisition_date: null,
      source_category_id: null,
      source_location_id: null
    })
  }
  quickAddData.value = null
  itemSearch.value = ''
  searchResults.value = []
}

function onQuickAddCommit(item) {
  emit('update:modelValue', {
    ...props.modelValue,
    itemid: `CUSTOM-${Date.now()}`,
    SKU: item.SKU || '',
    memo: item.memo || '',
    cogs: item.cogs || 0,
    photo_url: '',
    unit_price: item.unit_price || 0,
    is_custom: true,
    source_price: item.source_price || 0,
    acquisition_date: item.acquisition_date || null,
    source_category_id: item.source_category_id || null,
    source_location_id: item.source_location_id || null
  })
  quickAddMode.value = false
  itemSearch.value = ''
  searchResults.value = []
}

function selectItem(item) {
  emit('update:modelValue', {
    ...props.modelValue,
    itemid:             item.remote_id || '',
    SKU:                item.SKU || '',
    memo:               item.title || '',
    cogs:               item.source_price || 0,
    photo_url:          item.photo_url || '',
    unit_price:         item.sale_price || 0,
    available_quantity: parseInt(item.available_quantity ?? 0),
  })
  showResults.value = false
  itemSearch.value = ''
  searchResults.value = []
}

function clearItem() {
  if (props.modelValue.is_custom) {
    // Re-open quick add form pre-filled with current values
    quickAddData.value = { ...props.modelValue }
    quickAddKey.value++
    emit('update:modelValue', {
      ...props.modelValue,
      itemid: '',
    })
    quickAddMode.value = true
  } else {
    emit('update:modelValue', {
      ...props.modelValue,
      itemid: '',
      SKU: '',
      memo: '',
      cogs: 0,
      photo_url: '',
      unit_price: 0
    })
    itemSearch.value = ''
    searchResults.value = []
  }
}

function searchItem(val) {
  if (searching.value) return
  searching.value = true
  searchResults.value = []

  store.dispatch('eshop/searchInventory', { val, includeDelist: includeDelisted.value })
    .then(results => {
      searchResults.value = results
      showResults.value = true
      searching.value = false
    })
    .catch(err => {
      console.error('Search error:', err)
      searching.value = false
    })
}

let searchTimeout = null

watch(itemSearch, val => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  if (val.length > 2) {
    searchTimeout = setTimeout(() => searchItem(val), 500)
  } else {
    searchResults.value = []
  }
})

watch(includeDelisted, () => {
  if (itemSearch.value.length > 2) {
    searchItem(itemSearch.value)
  }
})

// Close results when clicking outside
function handleClickOutside(event) {
  if (!event.target.closest('.item-search')) {
    showResults.value = false
  }
}

watch(showResults, (newVal) => {
  if (newVal) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/sass/_variables-esc.scss';

.search-results {
  max-height: 400px;
  overflow-y: auto;
}

.item-search {
  position: relative;
}
</style>
